// src/DeliveryScreen.jsx
import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/logo.png";
import { api } from "./api/api";
import { formatNumber } from "./utils/numberFormat";

const formatDateToYMD = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return "";
  // The date from the database can be a full ISO string or just a date part.
  // Slicing the first 10 characters is the most robust way to get YYYY-MM-DD
  // without involving any timezone conversions from `new Date()`.
  return dateString.slice(0, 10);
};

const getTodayYMD = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function generateInvoicePdf({ header, invoice, recipientName, deliveryDate, days, discount = 0, d_no }) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();

  // ===== 1) Logo =====
  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;

  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  // ===== 2) Title =====
  let y = logoY + logoHeight + 20;

  doc.setFontSize(16);
  doc.text("Storage Charges Invoice", pageWidth / 2, y, { align: "center" });

  doc.setFontSize(11);
  y += 20;

  // ===== 3) Header info =====
  doc.text(`Voyage No.: ${header.voyage_number || ""}`, 80, y);
  doc.text(`Arrival Date: ${formatDateToYMD(header.date_of_arrival) || ""}`, 150, y);

    if (d_no) {
    doc.text(`Invoice No: ${d_no}`, 14, y);
  }
  y += 6;
  doc.text(`Days of Storage: ${days}`, 150, y);

  y += 6;
  const consigneeText = `Consignee / Receiver: ${recipientName || ""}`;
  const consigneeLines = doc.splitTextToSize(consigneeText, 130);
  doc.text(consigneeLines, 14, y);
  doc.text(`Delivery Date: ${formatDateToYMD(deliveryDate) || ""}`, 150, y);

  if (consigneeLines.length > 1) {
    const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    y += (consigneeLines.length - 1) * lineHeight;
  }

  if (invoice.service) {
    y += 6;
    doc.text(`Service: ${invoice.service.name}`, 14, y);
    doc.text(`Rate per day: ${formatAmount(invoice.service.price)}`, 150, y);
  }

  const finalAmount = invoice.total - discount;

  // ===== 4) Table =====
  const tableBody = invoice.lines.map(
    ({ bol, unitPrice, qty, weight, amount }) => [
      bol.bol_reference || "",
      bol.goods_description || "",
      bol.consignee_name || "",
      formatNumber(qty || 0, 0),
      formatNumber(weight || 0, 2),
      formatNumber(unitPrice || 0, 2),
      days,
      formatNumber(finalAmount, 2),
    ]
  );

  autoTable(doc, {
    startY: y + 20,
    head: [
      [
        "AWB Ref.",
        "Goods Description",
        "Consignee",
        "Packages",
        "Weight",
        "Rate/day",
        "Days",
        "Total Amount",
      ],
    ],
    body: tableBody,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 165, 245], textColor: 255 },
  });

  const fileName = `ManifestInvoice_${header.id}.pdf`;
  doc.save(fileName);
}

function DeliveryScreen({ onBack }) {
  const [pendingBols, setPendingBols] = useState([]);
  const [selectedBol, setSelectedBol] = useState(null);
  const [header, setHeader] = useState(null);
  const [days, setDays] = useState(1);

  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [recipientName, setRecipientName] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(getTodayYMD());
  
  const [subtractOneDay, setSubtractOneDay] = useState(false);
  const [discount, setDiscount] = useState(0);

  const [message, setMessage] = useState(null);

  // Fetch pending BOLs
  useEffect(() => {
    const fetchPending = async () => {
      try {
        setLoading(true);
        const res = await api.get("/bols/pending");
        const list =
          Array.isArray(res) ? res :
          Array.isArray(res?.data) ? res.data :
          Array.isArray(res?.rows) ? res.rows :
          [];
        setPendingBols(list);
      } catch (err) {
        console.error(err);
        setPendingBols([]);
        setMessage({ type: "error", text: err.message || "Error loading pending AWBs" });
      } finally {
        setLoading(false);
      }
    };
    fetchPending();
  }, []);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await api.get("/services");
        setServices(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchServices();
  }, []);

  // Days calculation (inclusive)
  useEffect(() => {
    if (!header || !header.date_of_arrival || !deliveryDate) return;

    // Explicitly format to YYYY-MM-DD before parsing
    const arrivalDateStr = formatDateToYMD(header.date_of_arrival);
    const deliveryDateStr = formatDateToYMD(deliveryDate);

    if (!arrivalDateStr || !deliveryDateStr) {
      setDays(1);
      return;
    }
    
    const [arrY, arrM, arrD] = arrivalDateStr.split('-').map(Number);
    const [delY, delM, delD] = deliveryDateStr.split('-').map(Number);

    // Create dates with UTC to avoid timezone issues in calculation.
    // Date.UTC returns a timestamp.
    const arrival = Date.UTC(arrY, arrM - 1, arrD);
    const delivery = Date.UTC(delY, delM - 1, delD);

    const diffMs = delivery - arrival;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1; // inclusive
    
    setDays(diffDays < 1 ? 1 : diffDays);
  }, [header, deliveryDate]);

  const handleSelectBol = async (bolId) => {
    try {
      setLoading(true);
      setMessage(null);
      const data = await api.get(`/bols/${bolId}/details`);
      setHeader(data.header);
      setSelectedBol(data.bol || null);
      const defaultRecipient = data.bol?.consignee_name || "";
      setRecipientName(defaultRecipient);
      setDeliveryDate(getTodayYMD());
      setSelectedServiceId(data.bol && data.bol.service_id ? String(data.bol.service_id) : "");
      setDays(data.days || 1);
      
      // Reset new fields
      // قراءة حالة التشيك بوكس من قاعدة البيانات (1 = true, 0 = false)
      setSubtractOneDay(!!data.bol.day);
      setDiscount(0);
      
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err.message || "Error loading AWB details" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeliverBol = async () => {
    if (!selectedBol || !header) return;

    if (!selectedServiceId) {
      setMessage({ type: "error", text: "Please select a service before generating the invoice" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const finalDays = Math.max(1, subtractOneDay ? days - 1 : days);

      const data = await api.post(`/bols/${selectedBol.id}/deliver`, {
        recipientName,
        deliveryDate,
        serviceId: Number(selectedServiceId),
        days: finalDays,
        discount: Number(discount) || 0,
        day: subtractOneDay ? 1 : 0,
      });

      try {
        const invoice = {
          service: data.service,
          total: data.total,
          lines: [
            {
              bol: {
                bol_reference: data.bol.bol_reference,
                goods_description: data.bol.goods_description,
                consignee_name: data.bol.consignee_name,
              },
              unitPrice: data.line.unitPrice,
              qty: data.bol.number_of_packages,
              weight: data.bol.gross_mass,
              amount: data.line.amount,
            },
          ],
        };

        generateInvoicePdf({
          header: data.header,
          invoice,
          recipientName: data.recipientName,
          deliveryDate: data.deliveryDate,
          days: finalDays,
          discount: discount,
          d_no: data.bol.d_no,
        });
      } catch (pdfErr) {
        console.error("PDF generation error:", pdfErr);
      }

      setMessage({ type: "success", text: "Invoice generated successfully ✅" });

      setPendingBols((prev) => prev.filter((row) => row.bol_id !== selectedBol.id));
      setModalOpen(false);
      setSelectedBol(null);
      setHeader(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Server connection error during delivery" });
    } finally {
      setLoading(false);
    }
  };

  const [searchAwb, setSearchAwb] = useState("");

  const filteredPending = useMemo(() => {
    const list = Array.isArray(pendingBols) ? pendingBols : [];
    const q = searchAwb.trim().toLowerCase();
    if (!q) return list;
    return list.filter((b) =>
      String(b.bol_reference || "").toLowerCase().includes(q)
    );
  }, [pendingBols, searchAwb]);

  const finalDays = useMemo(() => {
    // إذا تم تفعيل خيار إنقاص يوم، نقوم بخصم 1 من إجمالي الأيام المحسوبة
    // days هو الفرق بين الوصول والتسليم + 1 (شامل اليومين)
    // عند الخصم يصبح (الفرق + 1) - 1 = الفرق فقط
    const d = subtractOneDay ? days - 1 : days;
    return Math.max(1, d);
  }, [days, subtractOneDay]);

  return (
    <div className="screen screen-rtl">
      <div className="screen-header">
        <h2>Delivery / Invoice</h2>
      </div>

      {message && (
        <div className={`message ${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="grid grid-auto small-gap">
          <div className="field">
            <label>Search by AWB</label>
            <input
              value={searchAwb}
              onChange={(e) => setSearchAwb(e.target.value)}
              placeholder="Type AWB reference..."
            />
          </div>
          <div className="field">
            <label>Results</label>
            <input value={filteredPending.length} readOnly />
          </div>
          <div className="field" style={{ alignSelf: "end" }}>
            <button type="button" onClick={() => setSearchAwb("")}>
              Clear
            </button>
          </div>
        </div>

        <h3>Pending AWBs</h3>

        {loading && <p>Loading...</p>}
        {!loading && pendingBols.length === 0 && (
          <p className="hint-text">No pending BOLs.</p>
        )}

        {!loading && pendingBols.length > 0 && (
          <div className="table-scroll-y">
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>AWB</th>
                    <th>Consignee</th>
                    <th>Packages</th>
                    <th>Gross Weight</th>
                    <th>Voyage No.</th>
                    <th>Arrival Date</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map((row) => (
                    <tr key={row.bol_id}>
                      <td>{row.bol_reference}</td>
                      <td>{row.consignee_name}</td>
                      <td>{row.number_of_packages}</td>
                      <td>{row.gross_mass}</td>
                      <td>{row.voyage_number}</td>
                      <td>{formatDateToYMD(row.date_of_arrival)}</td>
                      <td>{row.place_of_departure_code}</td>
                      <td>{row.place_of_destination_code}</td>
                      <td>
                        <button type="button" onClick={() => handleSelectBol(row.bol_id)}>
                          Generate Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && filteredPending.length === 0 && (
                    <tr>
                      <td colSpan={9} className="hint-text">No matching AWBs.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modalOpen && header && selectedBol && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3>Invoice for AWB #{selectedBol.bol_reference}</h3>
              <button type="button" className="secondary-btn" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>
            <div className="modal-body">
              <div className="card">
                <h4>Header Information</h4>
                <div className="grid grid-auto small-gap">
                  <div><strong>Voyage No.:</strong> {header.voyage_number}</div>
                  <div><strong>Arrival Date:</strong> {formatDateToYMD(header.date_of_arrival)}</div>
                  <div><strong>From:</strong> {header.place_of_departure_code}</div>
                  <div><strong>To:</strong> {header.place_of_destination_code}</div>
                  <div><strong>Days:</strong> {finalDays}</div>
                </div>
              </div>

              <div className="card">
                <h4>Delivery & Service</h4>
                <div className="grid grid-auto small-gap">
                  <div className="field">
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder=" "
                    />
                    <label>Receiver Name (default: consignee)</label>
                  </div>
                  <div className="field">
                    <input
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      placeholder=" "
                    />
                    <label>Delivery Date</label>
                  </div>
                  <div className="field select-field">
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                    >
                      <option value="">Select Service</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <label>Service</label>
                  </div>
                </div>
                <div className="grid grid-auto small-gap" style={{ marginTop: '1rem', alignItems: 'center' }}>
                    <div className="field-checkbox">
                        <input
                            id="subtract-day-ch"
                            type="checkbox"
                            checked={subtractOneDay}
                            onChange={(e) => setSubtractOneDay(e.target.checked)}
                        />
                        <label htmlFor="subtract-day-ch">Subtract one day from total</label>
                    </div>
                    <div className="field">
                        <input
                            type="number"
                            value={discount}
                            onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                            placeholder=" "
                            style={{textAlign: 'right'}}
                        />
                        <label>Discount Amount</label>
                    </div>
                </div>
              </div>

              <div className="card">
                <h4>AWB Details (Estimated)</h4>

                <div className="table-scroll-y modal-table-scroll">
                  <div className="table-wrap"></div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>AWB</th>
                        <th>Goods Description</th>
                        <th>Consignee</th>
                        <th>Days</th>
                        <th>Packages</th>
                        <th>Gross Weight</th>
                        <th>Rate/Day</th>
                        <th>Estimated Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const service = services.find((s) => s.id === Number(selectedServiceId));
                        const unitPrice = service ? Number(service.price) : 0;
                        const weight = Number(selectedBol.gross_mass) || 0;
                        const qty = Number(selectedBol.number_of_packages) || 0;
                        const amount = unitPrice * finalDays * weight;

                        return (
                          <tr>
                            <td>{selectedBol.bol_reference}</td>
                            <td>{selectedBol.goods_description}</td>
                            <td>{selectedBol.consignee_name}</td>
                            <td>{finalDays}</td>
                            <td>{formatNumber(qty, 0)}</td>
                            <td>{formatNumber(weight, 2)}</td>
                            <td>{formatNumber(unitPrice, 2)}</td>
                            <td>{formatNumber(amount, 2)}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {(() => {
                    const service = services.find((s) => s.id === Number(selectedServiceId));
                    const unitPrice = service ? Number(service.price) : 0;
                    const weight = Number(selectedBol.gross_mass) || 0;
                    const amount = unitPrice * finalDays * weight;
                    const totalAfterDiscount = amount - discount;
                    return (
                        <div style={{textAlign: 'right', marginTop: '1rem', paddingRight: '1rem'}}>
                            <h4>Total Amount: {formatNumber(totalAfterDiscount, 2)}</h4>
                        </div>
                    )
                })()}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={handleDeliverBol}
                disabled={loading || !selectedServiceId}
              >
                {loading ? "Processing..." : "Generate Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryScreen;